<template>
  <header class="app-topbar">
    <div class="topbar-left">
      <RouterLink class="app-brand" to="/">
        <BookOpen class="icon" />
        <span>{{ session.knowledgeBaseTitle }}</span>
      </RouterLink>
    </div>

    <nav v-if="session.isAuthenticated" class="topbar-switch" aria-label="View">
      <RouterLink :class="['topbar-switch-item', { active: inChanges }]" to="/changes">
        <History class="icon" />
        <span>Recent changes</span>
      </RouterLink>
      <RouterLink :class="['topbar-switch-item', { active: !inChanges }]" to="/">
        <Files class="icon" />
        <span>Workspace</span>
      </RouterLink>
      <button
        type="button"
        class="topbar-switch-item topbar-switch-search"
        title="Search workspace"
        @click="openPalette"
      >
        <Search class="icon" />
        <span>Search</span>
        <span class="topbar-search-key">{{ shortcutHint }}</span>
      </button>
    </nav>

    <div v-if="session.isAuthenticated" class="topbar-right">
      <NotificationsBell />
      <details v-if="session.profile" ref="profileMenu" class="profile-menu">
      <summary class="profile-chip" title="User menu">
        <UserAvatar :name="session.profile.name" size="profile" aria-hidden="true" />
        <span class="profile-name">{{ session.profile.name }}</span>
        <ChevronDown class="icon profile-caret" />
      </summary>
      <div class="profile-popover">
        <RouterLink to="/settings/profile" @click="closeProfileMenu">
          <UserRound class="icon" />
          <span>Profile</span>
        </RouterLink>
        <RouterLink v-if="session.canUseAdministration" :to="adminPath" @click="closeProfileMenu">
          <Settings class="icon" />
          <span>Workspace settings</span>
        </RouterLink>
        <div class="theme-actions" role="group" aria-label="Theme">
          <button type="button" :class="themeClass(ThemeMode.AUTO)" @click="setTheme(ThemeMode.AUTO)">
            <Monitor class="icon" />
            <span>Auto</span>
          </button>
          <button type="button" :class="themeClass(ThemeMode.LIGHT)" @click="setTheme(ThemeMode.LIGHT)">
            <Sun class="icon" />
            <span>Light</span>
          </button>
          <button type="button" :class="themeClass(ThemeMode.DARK)" @click="setTheme(ThemeMode.DARK)">
            <Moon class="icon" />
            <span>Dark</span>
          </button>
        </div>
        <button type="button" class="menu-command danger" @click="logout">
          <LogOut class="icon" />
          <span>Logout</span>
        </button>
      </div>
    </details>
    </div>
    <RouterLink v-else-if="session.initialized && !session.isAuthenticated" class="login-link" to="/login">Log in</RouterLink>
  </header>

  <div v-if="paletteOpen" class="palette-backdrop" @click.self="closePalette" @keydown.esc="closePalette">
    <section class="palette" role="dialog" aria-modal="true" aria-label="Search workspace">
      <div class="palette-input-row">
        <Search class="icon" />
        <input
          ref="paletteInput"
          v-model="paletteQuery"
          type="text"
          maxlength="200"
          placeholder="Search documents and folders…"
          @keydown.down.prevent="moveHighlight(1)"
          @keydown.up.prevent="moveHighlight(-1)"
          @keydown.enter.prevent="openHighlighted"
          @keydown.esc="closePalette"
        />
        <button type="button" class="icon-button" aria-label="Close" @click="closePalette">
          <X class="icon" />
        </button>
      </div>
      <div class="palette-list">
        <button
          v-for="(item, index) in paletteItems"
          :key="`${item.kind}:${item.id}`"
          type="button"
          :class="['palette-item', { highlighted: index === highlightedIndex }]"
          @mouseenter="highlightedIndex = index"
          @click="openItem(item)"
        >
          <Folder v-if="item.kind === WorkspaceSearchResultKind.FOLDER" class="icon" />
          <FileText v-else class="icon" />
          <span class="palette-item-copy">
            <span class="palette-item-title">
              <template v-for="(part, partIndex) in highlightedParts(item.title)" :key="partIndex">
                <mark v-if="part.highlighted">{{ part.text }}</mark>
                <template v-else>{{ part.text }}</template>
              </template>
            </span>
            <span v-if="item.snippet" class="palette-item-snippet">
              <template v-for="(part, partIndex) in highlightedParts(item.snippet)" :key="partIndex">
                <mark v-if="part.highlighted">{{ part.text }}</mark>
                <template v-else>{{ part.text }}</template>
              </template>
            </span>
          </span>
          <span class="palette-item-project">{{ itemLocation(item) }}</span>
        </button>
        <div v-if="paletteLoading" class="palette-empty" aria-live="polite">
          <LoaderCircle class="icon palette-spinner" />
          <span>Searching…</span>
        </div>
        <div v-else-if="paletteError" class="palette-empty" aria-live="polite">
          <SearchX class="icon" />
          <span>{{ paletteError }}</span>
        </div>
        <div v-else-if="paletteItems.length === 0" class="palette-empty" aria-live="polite">
          <SearchX class="icon" />
          <span>No workspace items match “{{ paletteQuery }}”.</span>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import {
  BookOpen, ChevronDown, FileText, Files, Folder, History, LoaderCircle, LogOut, Monitor, Moon, Search, SearchX, Settings, Sun,
  UserRound, X
} from 'lucide-vue-next';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import type { EntityId } from '../api/entityId';
import {
  CancelError,
  PermissionKind,
  QueriesService,
  WorkspaceSearchResultKind,
  type CancelablePromise,
  type SearchWorkspaceQueryResponse,
  type WorkspaceSearchResultResponse
} from '../api/generated';
import { getErrorMessage, useSessionStore } from '../stores/session';
import { useWorkspaceStore, type WorkspaceFolderTree } from '../stores/workspace';
import { getRecentDocuments } from '../stores/workspaceViewport';
import { currentThemeMode, setThemeMode, ThemeMode, type ThemeMode as ThemeModeValue } from '../theme';
import NotificationsBell from './NotificationsBell.vue';
import UserAvatar from './UserAvatar.vue';

interface PaletteItem {
  kind: WorkspaceSearchResultKind;
  id: EntityId;
  projectId: EntityId;
  title: string;
  project: string;
  path: string;
  snippet: string | null;
}

interface HighlightedPart {
  text: string;
  highlighted: boolean;
}

const router = useRouter();
const route = useRoute();
const session = useSessionStore();

const inChanges = computed(() => route.path === '/changes');
const workspace = useWorkspaceStore();
const profileMenu = ref<HTMLDetailsElement | null>(null);
const paletteOpen = ref(false);
const paletteQuery = ref('');
const highlightedIndex = ref(0);
const paletteInput = ref<HTMLInputElement | null>(null);
const recentIds = ref<EntityId[]>([]);
const searchResults = ref<PaletteItem[]>([]);
const paletteLoading = ref(false);
const paletteError = ref<string | null>(null);
let paletteSearchTimer: number | null = null;
let paletteSearchRequest: CancelablePromise<SearchWorkspaceQueryResponse> | null = null;
let paletteSearchGeneration = 0;

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const shortcutHint = computed(() => (isMac ? '⌘F' : 'Ctrl F'));

const adminPath = computed(() => {
  if (session.hasGlobalPermission(PermissionKind.MANAGE_SETTINGS)) {
    return '/settings/general';
  }

  if (session.hasGlobalPermission(PermissionKind.CREATE_PROJECT)
    || session.hasGlobalPermission(PermissionKind.MANAGE_PROJECT_ACCESS)) {
    return '/settings/projects';
  }

  if (session.hasGlobalPermission(PermissionKind.MANAGE_USERS)) {
    return '/settings/users';
  }

  return '/settings/roles';
});

const allDocuments = computed<PaletteItem[]>(() => {
  const result: PaletteItem[] = [];
  for (const project of workspace.workspace?.projects ?? []) {
    for (const document of project.documents) {
      result.push({
        kind: WorkspaceSearchResultKind.DOCUMENT,
        id: document.id,
        projectId: project.id,
        title: document.title,
        project: project.name,
        path: document.title,
        snippet: null
      });
    }

    collectFolderDocuments(project.folders, project.id, project.name, result);
  }

  return result.sort((left, right) => left.title.localeCompare(right.title, undefined, { sensitivity: 'base' }));
});

function recentRank(documentId: EntityId): number {
  const index = recentIds.value.indexOf(documentId);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

function byRecentThenTitle(left: PaletteItem, right: PaletteItem): number {
  const rankDifference = recentRank(left.id) - recentRank(right.id);
  return rankDifference !== 0
    ? rankDifference
    : left.title.localeCompare(right.title, undefined, { sensitivity: 'base' });
}

const paletteItems = computed<PaletteItem[]>(() => {
  return paletteQuery.value.trim()
    ? searchResults.value
    : [...allDocuments.value].sort(byRecentThenTitle).slice(0, 50);
});

function collectFolderDocuments(
  folders: WorkspaceFolderTree[],
  projectId: EntityId,
  projectName: string,
  output: PaletteItem[]
): void {
  for (const folder of folders) {
    for (const document of folder.documents) {
      output.push({
        kind: WorkspaceSearchResultKind.DOCUMENT,
        id: document.id,
        projectId,
        title: document.title,
        project: projectName,
        path: document.title,
        snippet: null
      });
    }

    collectFolderDocuments(folder.folders, projectId, projectName, output);
  }
}

watch(paletteItems, () => {
  highlightedIndex.value = 0;
});

watch(paletteQuery, value => {
  scheduleSearch(value);
});

function openPalette(): void {
  paletteOpen.value = true;
  paletteQuery.value = '';
  highlightedIndex.value = 0;
  searchResults.value = [];
  paletteError.value = null;
  paletteLoading.value = false;
  const userId = session.profile?.id;
  recentIds.value = userId != null ? getRecentDocuments(userId) : [];
  void nextTick(() => paletteInput.value?.focus());
}

function closePalette(): void {
  cancelPaletteSearch();
  paletteOpen.value = false;
}

function moveHighlight(delta: number): void {
  const count = paletteItems.value.length;
  if (count === 0) {
    return;
  }

  highlightedIndex.value = (highlightedIndex.value + delta + count) % count;
}

async function openHighlighted(): Promise<void> {
  const item = paletteItems.value[highlightedIndex.value];
  if (item) {
    await openItem(item);
  }
}

async function openItem(item: PaletteItem): Promise<void> {
  if (item.kind === WorkspaceSearchResultKind.FOLDER) {
    await openFolder(item);
    return;
  }

  await openDocument(item.id);
}

async function openDocument(documentId: EntityId): Promise<void> {
  closePalette();
  await router.push({ name: 'workspace-document', params: { documentId: String(documentId) } });
}

async function openFolder(item: PaletteItem): Promise<void> {
  closePalette();
  try {
    await workspace.revealFolder(item.projectId, item.id);
    await router.push('/');
    await nextTick();
    const rows = document.querySelectorAll<HTMLElement>('[data-sidebar-folder-id]');
    for (const row of rows) {
      if (row.dataset.sidebarFolderId === item.id) {
        row.scrollIntoView({ block: 'nearest' });
        row.querySelector<HTMLButtonElement>('.tree-label')?.focus();
        break;
      }
    }
  } catch (error) {
    workspace.error = getErrorMessage(error, 'Unable to reveal folder.');
  }
}

function scheduleSearch(value: string): void {
  cancelPaletteSearch();
  const text = value.trim();
  const generation = ++paletteSearchGeneration;
  searchResults.value = [];
  paletteError.value = null;
  if (!text) {
    paletteLoading.value = false;
    return;
  }

  paletteLoading.value = true;
  paletteSearchTimer = window.setTimeout(() => {
    paletteSearchTimer = null;
    void searchWorkspace(text, generation);
  }, 200);
}

async function searchWorkspace(text: string, generation: number): Promise<void> {
  const request = QueriesService.querySearchWorkspaceQuery({ requestBody: { text } });
  paletteSearchRequest = request;
  try {
    const response = await request;
    if (generation === paletteSearchGeneration) {
      searchResults.value = response.results.map(toPaletteItem);
    }
  } catch (error) {
    if (!(error instanceof CancelError) && generation === paletteSearchGeneration) {
      paletteError.value = getErrorMessage(error, 'Unable to search the workspace.');
    }
  } finally {
    if (generation === paletteSearchGeneration) {
      paletteLoading.value = false;
    }

    if (paletteSearchRequest === request) {
      paletteSearchRequest = null;
    }
  }
}

function cancelPaletteSearch(): void {
  if (paletteSearchTimer != null) {
    window.clearTimeout(paletteSearchTimer);
    paletteSearchTimer = null;
  }

  paletteSearchRequest?.cancel();
  paletteSearchRequest = null;
  paletteSearchGeneration++;
  paletteLoading.value = false;
}

function toPaletteItem(result: WorkspaceSearchResultResponse): PaletteItem {
  return {
    kind: result.kind,
    id: String(result.id),
    projectId: String(result.projectId),
    title: result.title,
    project: result.projectName,
    path: result.path,
    snippet: result.snippet ?? null
  };
}

function itemLocation(item: PaletteItem): string {
  return item.path === item.title ? item.project : `${item.project} · ${item.path}`;
}

function highlightedParts(text: string): HighlightedPart[] {
  const terms = paletteQuery.value.trim().split(/\s+/).filter(Boolean);
  if (terms.length === 0) {
    return [{ text, highlighted: false }];
  }

  const expression = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'giu');
  return text
    .split(expression)
    .map((part, index) => ({ text: part, highlighted: index % 2 === 1 }))
    .filter(part => part.text.length > 0);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function closeProfileMenu(): void {
  if (profileMenu.value) {
    profileMenu.value.open = false;
  }
}

function themeClass(themeMode: ThemeModeValue): string {
  return currentThemeMode.value === themeMode ? 'active' : '';
}

function setTheme(themeMode: ThemeModeValue): void {
  setThemeMode(themeMode);
}

async function logout(): Promise<void> {
  closeProfileMenu();
  await session.logout();
  workspace.$reset();
  await router.push('/login');
}

function closeIfOutside(event: PointerEvent): void {
  const element = profileMenu.value;
  if (!element?.open || !(event.target instanceof Node) || element.contains(event.target)) {
    return;
  }

  element.open = false;
}

function onShortcut(event: KeyboardEvent): void {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'f') {
    if (!session.isAuthenticated) {
      return;
    }

    event.preventDefault();
    if (paletteOpen.value) {
      closePalette();
    } else {
      openPalette();
    }
  }
}

onMounted(() => {
  document.addEventListener('pointerdown', closeIfOutside);
  document.addEventListener('keydown', onShortcut);
});
onBeforeUnmount(() => {
  cancelPaletteSearch();
  document.removeEventListener('pointerdown', closeIfOutside);
  document.removeEventListener('keydown', onShortcut);
});
</script>
